import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  addProject,
  addProjectMatrixItemInList,
  bulkUpdateProjectMatrixItemsStatuses,
  checkCurrentProjectForPendingChanges,
  createProject,
  createProjectMatrixItem,
  deleteProject,
  deleteProjectMatrixItem,
  deleteProjectMatrixItemInList,
  deleteStudyAdmin,
  getProjectMatrixItems,
  getProjectStatusHistory,
  getRecordForms,
  getStudyAdminList,
  loadProjectList,
  loadProjectMatrixItemsCommentsState,
  pendingCreateProject,
  populateCurrentProject,
  populateCurrentProjectSnapshot,
  populateProjectMatrixItems,
  populateProjectMatrixItemsCommentsState,
  populateProjectStatusHistory,
  populateProjectStatusInList,
  populateRecordForms,
  populateStudyAdminList,
  publishAmendedProject,
  publishProject,
  refreshCurrentProject,
  removeProject,
  removeStudyAdminFromList,
  searchProjectById,
  successCreateProject,
  syncStudies,
  updateEntitiesStatuses,
  updateProject,
  updateProjectField,
  updateProjectFields,
  updateProjectList,
  updateProjectMatrixItem,
  updateProjectMatrixItemInList,
  updateProjectStatus,
  validateStudyAndRelease,
} from './project.actions';

import { AppState, getState } from '../../store/models/app.state';
import { Store } from '@ngrx/store';
import { ProjectService } from '../project.service';
import { catchError, exhaustMap, mergeMap, tap } from 'rxjs/operators';
import { ProjectModel } from '../project.model';
import { addLoader, errorPopup, fetchCurrentSponsor, globalLoading, removeLoader, successPopup } from '../../store/actions/ui.actions';
import { getUsersByOrganization } from '../../user/store/user.actions';
import { isEqual, omit } from 'lodash-es';
import { IProjectStatus } from '../project-status.interface';
import { Statuses } from '../../shared/models/statuses.enum';
import { IStudyMatrixItem } from '../../study-matrix/study-matrix-editor/study-matrix-item.interface';
import { UtilsService } from '../../core/utils.service';

@Injectable()
export class ProjectEffect {
  actions = inject(Actions);
  projectService = inject(ProjectService);
  store = inject(Store<AppState>);
  utilsService = inject(UtilsService);

  searchProjectById = createEffect(() => {
    return this.actions.pipe(
      ofType(searchProjectById),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      mergeMap(action =>
        this.projectService.searchById(action.id).pipe(
          mergeMap((res: ProjectModel) => [
            populateCurrentProject({ project: res }),
            populateCurrentProjectSnapshot({ project: res }),
            removeLoader(action),
            globalLoading(false),
          ]),
          catchError(() => {
            return [removeLoader(action), globalLoading(false)];
          }),
        ),
      ),
    );
  });

  createProject = createEffect(() => {
    return this.actions.pipe(
      ofType(createProject),
      mergeMap(({ project }) =>
        this.projectService.create(project).pipe(
          mergeMap((res: ProjectModel) => [
            addProject({ project: res }),
            pendingCreateProject({ pending: false }),
            populateCurrentProject({ project: res }),
            populateCurrentProjectSnapshot({ project: res }),
            successCreateProject({ success: true }),
            globalLoading(false),
            successPopup({ message: 'STUDY.MESSAGES.STUDY_CREATED' }),
          ]),
          catchError(() => {
            return [globalLoading(false), errorPopup({ error: 'STUDY.MESSAGES.UNABLE_TO_CREATE_STUDY' })];
          }),
        ),
      ),
    );
  });

  updateProject = createEffect(() => {
    return this.actions.pipe(
      ofType<ReturnType<typeof updateProject>>(updateProject),
      mergeMap(({ project }) =>
        this.projectService.update(project).pipe(
          mergeMap(res => [
            populateCurrentProject({ project: res }),
            populateCurrentProjectSnapshot({ project: res }),
            pendingCreateProject({ pending: false }),
            successCreateProject({ success: true }),
            successPopup({ message: 'STUDY.MESSAGES.STUDY_UPDATED' }),
            globalLoading(false),
          ]),
          catchError(() => [
            globalLoading(false),
            successCreateProject({ success: false }),
            pendingCreateProject({ pending: false }),
            errorPopup({ error: 'STUDY.MESSAGES.UNABLE_TO_UPDATE_STUDY' }),
          ]),
        ),
      ),
    );
  });

  updateProjectStatus = createEffect(() => {
    return this.actions.pipe(
      ofType<ReturnType<typeof updateProjectStatus>>(updateProjectStatus),
      mergeMap(({ projectId, newStatus }) =>
        this.projectService.updateProjectStatus(projectId, newStatus).pipe(
          mergeMap((status: IProjectStatus) => {
            return [
              updateProjectField({ field: 'status', value: status }),
              populateProjectStatusInList({ status }),
              getProjectStatusHistory({ projectId }),
              pendingCreateProject({ pending: false }),
              successCreateProject({ success: true }),
              successPopup({ message: 'STUDY.MESSAGES.STUDY_STATUS_UPDATED' }),
              globalLoading(false),
            ];
          }),
          catchError(() => [
            globalLoading(false),
            successCreateProject({ success: false }),
            pendingCreateProject({ pending: false }),
            errorPopup({ error: 'STUDY.MESSAGES.UNABLE_TO_UPDATE_STUDY_STATUS' }),
          ]),
        ),
      ),
    );
  });

  getProjectStatusHistory = createEffect(() => {
    return this.actions.pipe(
      ofType<ReturnType<typeof getProjectStatusHistory>>(getProjectStatusHistory),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      mergeMap(action =>
        this.projectService.getProjectAllStatuses(action.projectId).pipe(
          mergeMap((statuses: IProjectStatus[]) => [populateProjectStatusHistory({ statuses }), removeLoader(action)]),
          catchError(() => [removeLoader(action)]),
        ),
      ),
    );
  });

  loadProjectList = createEffect(() => {
    return this.actions.pipe(
      ofType(loadProjectList),
      mergeMap(() =>
        this.projectService.getProjectList().pipe(
          mergeMap(res => [updateProjectList({ projectList: res }), globalLoading(false)]),
          catchError(() => [globalLoading(false)]),
        ),
      ),
    );
  });

  deleteProject = createEffect(() => {
    return this.actions.pipe(
      ofType(deleteProject),
      mergeMap(({ id }) =>
        this.projectService.deleteProject(id).pipe(
          mergeMap(() => [removeProject({ id }), successPopup({ message: 'STUDY.MESSAGES.STUDY_DELETED' })]),
          catchError(() => [errorPopup({ error: 'STUDY.MESSAGES.UNABLE_TO_DELETE_STUDY' })]),
        ),
      ),
    );
  });
  getStudyAdmins = createEffect(() => {
    return this.actions.pipe(
      ofType(getStudyAdminList),
      mergeMap(({ studyId }) =>
        this.projectService.getStudyAdmins(studyId).pipe(
          mergeMap(res => [populateStudyAdminList({ admins: res })]),
          catchError(() => []),
        ),
      ),
    );
  });

  deleteStudyAdmin = createEffect(() => {
    return this.actions.pipe(
      ofType(deleteStudyAdmin),
      mergeMap(({ id }) =>
        this.projectService.deleteStudyAdmin(id).pipe(
          mergeMap(() => [
            successPopup({ message: 'STUDY.MESSAGES.STUDY_ADMIN_DELETED' }),
            removeStudyAdminFromList({ id: id }),
          ]),
          catchError(() => [errorPopup({ error: 'STUDY.MESSAGES.UNABLE_TO_DELETE_STUDY_ADMIN' })]),
        ),
      ),
    );
  });

  getRecordForms = createEffect(() => {
    return this.actions.pipe(
      ofType(getRecordForms),
      mergeMap(({ datasetId, projectId }) =>
        this.projectService.getRecordFormsPerProject(projectId, datasetId).pipe(
          mergeMap(res => [populateRecordForms({ recordForms: res })]),
          catchError(() => []),
        ),
      ),
    );
  });

  refreshCurrentProject = createEffect(() => {
    return this.actions.pipe(
      ofType(refreshCurrentProject),
      mergeMap(() => [
        searchProjectById({
          id: getState(this.store).project.current.project.id,
        }),
      ]),
      catchError(() => []),
    );
  });

  updateProjectField = createEffect(() => {
    return this.actions.pipe(
      ofType(updateProjectField, updateProjectFields, successCreateProject, populateCurrentProjectSnapshot),
      mergeMap(() => {
        const current = omit(getState(this.store).project.current.project, ['userLastNameField', 'userFirstNameField']);
        const snapshot = omit(getState(this.store).project.current.projectSnapshot, [
          'userLastNameField',
          'userFirstNameField',
        ]);
        return [
          checkCurrentProjectForPendingChanges({
            hasChanges: !isEqual(current, snapshot),
          }),
        ];
      }),
      catchError(() => []),
    );
  });

  loadProjectMatrixItemsCommentsCounter = createEffect(() => {
    return this.actions.pipe(
      ofType(loadProjectMatrixItemsCommentsState),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      mergeMap(action =>
        this.projectService.getProjectMatrixItemsCommentsCounters(action.projectId).pipe(
          mergeMap(comments => [removeLoader(action), populateProjectMatrixItemsCommentsState({ states: comments })]),
          catchError(() => [removeLoader(action)]),
        ),
      ),
    );
  });

  getProjectMatrixItems = createEffect(() => {
    return this.actions.pipe(
      ofType(getProjectMatrixItems),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      mergeMap(action =>
        this.projectService.getProjectMatrixByProjectId(action.projectId).pipe(
          mergeMap((items: IStudyMatrixItem[]) => [
            populateProjectMatrixItems({ items: this.mergeMatrixItemsByGuid(items) }),
            loadProjectMatrixItemsCommentsState({ projectId: action.projectId, addLoader: action.addLoader }),
            removeLoader(action),
          ]),
          catchError(() => [removeLoader(action)]),
        ),
      ),
    );
  });

  createProjectMatrixItem = createEffect(() => {
    return this.actions.pipe(
      ofType(createProjectMatrixItem),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      mergeMap(action =>
        this.projectService.createProjectMatrixItem(action.item).pipe(
          mergeMap(item => [
            addProjectMatrixItemInList({ item }),
            action.projectId ? loadProjectMatrixItemsCommentsState({ projectId: action.projectId }) : null,
            removeLoader(action),
            globalLoading(false),
          ]),
          catchError(() => [removeLoader(action), globalLoading(false)]),
        ),
      ),
    );
  });

  updateProjectMatrixItem = createEffect(() => {
    return this.actions.pipe(
      ofType(updateProjectMatrixItem),
      mergeMap(({ item, projectId }) =>
        this.projectService.updateProjectMatrixItem(item).pipe(
          mergeMap(item => {
            return [
              updateProjectMatrixItemInList({ item }),
              projectId ? loadProjectMatrixItemsCommentsState({ projectId }) : null,
              globalLoading(false),
            ];
          }),
          catchError(() => [globalLoading(false)]),
        ),
      ),
    );
  });

  deleteProjectMatrixItem = createEffect(() => {
    return this.actions.pipe(
      ofType(deleteProjectMatrixItem),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      mergeMap(action =>
        this.projectService.deleteProjectMatrixItem(action.itemId).pipe(
          mergeMap(() => [
            deleteProjectMatrixItemInList({ itemId: action.itemId }),
            loadProjectMatrixItemsCommentsState({ projectId: action.projectId }),
            removeLoader(action),
            globalLoading(false),
          ]),
          catchError(() => [removeLoader(action), globalLoading(false)]),
        ),
      ),
    );
  });

  bulkUpdateProjectMatrixItemsStatuses = createEffect(() => {
    return this.actions.pipe(
      ofType(bulkUpdateProjectMatrixItemsStatuses),
      mergeMap(({ projectId, newStatus, ids }) =>
        this.projectService.bulkUpdateProjectMatrixItemsStatues(projectId, newStatus, ids).pipe(
          mergeMap(() => [getProjectMatrixItems({ projectId }), globalLoading(false)]),
          catchError(() => [globalLoading(false)]),
        ),
      ),
    );
  });

  syncStudies = createEffect(() => {
    return this.actions.pipe(
      ofType(syncStudies),
      exhaustMap(() =>
        this.projectService.syncStudies().pipe(
          mergeMap(() => [
            loadProjectList(),
            fetchCurrentSponsor(),
            getUsersByOrganization(),
            successPopup({
              message: 'STUDY.MESSAGES.STUDY_SYNCED',
            }),
          ]),
          catchError(() => [
            globalLoading(false),
            errorPopup({
              error: 'STUDY.MESSAGES.UNABLE_TO_SYNC_STUDIES',
            }),
          ]),
        ),
      ),
    );
  });

  updateEntitiesStatuses = createEffect(() => {
    return this.actions.pipe(
      ofType(updateEntitiesStatuses),
      mergeMap(({ projectId, newStatus }) =>
        this.projectService.updateAllEntitiesStatuses(projectId, newStatus).pipe(
          mergeMap(() => [globalLoading(false)]),
          catchError(() => [globalLoading(false)]),
        ),
      ),
    );
  });

  publishProject = createEffect(() => {
    return this.actions.pipe(
      ofType(publishProject),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      exhaustMap(action =>
        this.projectService.publishProject(action.projectId, action.environment).pipe(
          mergeMap(() => [
            updateProjectStatus({ projectId: action.projectId, newStatus: Statuses.Published }),
            removeLoader(action),
            globalLoading(false),
          ]),
          catchError(e => [
            globalLoading(false),
            removeLoader(action),
            errorPopup({
              error: this.utilsService.extractErrorMessage(
                e.error?.error || '',
                'STUDY.MESSAGES.UNABLE_TO_PUBLISH_STUDY',
              ),
            }),
          ]),
        ),
      ),
    );
  });
  publishAmendedProject = createEffect(() => {
    return this.actions.pipe(
      ofType(publishAmendedProject),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      exhaustMap(action =>
        this.projectService.publishAmendedProject(action.projectId, action.environment).pipe(
          mergeMap(() => [
            updateProjectStatus({ projectId: action.projectId, newStatus: Statuses.Published }),
            removeLoader(action),
            globalLoading(false),
            successPopup({ message: 'STUDY.MESSAGES.STUDY_PUBLISHED' }),
          ]),
          catchError(e => [
            globalLoading(false),
            removeLoader(action),
            errorPopup({
              error: this.utilsService.extractErrorMessage(
                e.error?.error || '',
                'STUDY.MESSAGES.UNABLE_TO_PUBLISH_STUDY',
              ),
            }),
          ]),
        ),
      ),
    );
  });

  validateStudy = createEffect(() => {
    return this.actions.pipe(
      ofType(validateStudyAndRelease),
      tap(action => {
        if (action.addLoader) {
          this.store.dispatch(addLoader(action));
        }
      }),
      mergeMap(action => {
        return this.projectService[action.update ? 'validateStudyUpdate' : 'validateStudyCreation'](
          action.projectId,
          action.environment,
        ).pipe(
          mergeMap(() => [
            updateProjectStatus({ projectId: action.projectId, newStatus: Statuses.Released }),
            removeLoader(action),
            globalLoading(false),
          ]),
          catchError(e => {
            return [
              globalLoading(false),
              removeLoader(action),
              errorPopup({
                error: this.utilsService.extractErrorMessage(
                  e.error?.error || '',
                  'STUDY.MESSAGES.UNABLE_TO_RELEASE_STUDY',
                ),
              }),
            ];
          }),
        );
      }),
    );
  });

  private mergeMatrixItemsByGuid(items: IStudyMatrixItem[]): IStudyMatrixItem[] {
    return items.reduce((acc, item: IStudyMatrixItem) => {
      if (!item.projectMatrixGuid) {
        acc.push(item);
      } else {
        const existingItem = acc.find((x: IStudyMatrixItem) => x.projectMatrixGuid === item.projectMatrixGuid);
        if (existingItem) {
          if (item.sofaVersionId > existingItem.sofaVersionId) {
            acc[acc.indexOf(existingItem)] = item;
          }
        } else {
          acc.push(item);
        }
      }
      return acc;
    }, []);
  }
}
